import { useFormValidationWithState } from '@makemydeal/dr-shared-ui-utils';
import type { ValidationContext, UseValidationEngineOptions } from '@makemydeal/dr-shared-ui-utils';
import * as contractingSelectors from '@makemydeal/dr-dash-store/selectors/contractingSelectors';
import * as saveStateValidationSelectors from '@makemydeal/dr-dash-store/selectors/saveValidationSelectors';
import { getTogglesWithoutSafetyChecks } from '@makemydeal/dr-shared-store/selectors/featureToggleSelectors';
import { getCurrentOfferType } from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { useSelector } from 'react-redux';
import { useEffect, useMemo } from 'react';

export type UseFormValidationProps = {
    ctx?: Partial<Omit<ValidationContext, 'values' | 'path' | 'flow'>>;
} & Omit<UseValidationEngineOptions, 'flow'>;

export const useFormValidation = ({ ctx, ...options }: UseFormValidationProps) => {
    const formValidation = useFormValidationWithState(options);

    const isContractingContext = useSelector(contractingSelectors.getIsContractingContext);
    const isOnSaveValidationFlow = useSelector(saveStateValidationSelectors.isOnSaveValidationFlow);
    const featureToggles = useSelector(getTogglesWithoutSafetyChecks);
    const offerType = useSelector(getCurrentOfferType);
    // Other basic context values, that would be used in most cases should be added here:
    // For exmaple: State, fuelType, vehicleCondition, etc.

    const flow = useMemo(() => {
        const flows = ['default'];
        if (isContractingContext) {
            flows.push('contract');
        }
        if (isOnSaveValidationFlow) {
            flows.push('save');
        }

        return flows.join('+');
    }, [isContractingContext, isOnSaveValidationFlow]);

    useEffect(() => {
        formValidation.engine.setFlow(flow);
    }, [formValidation.engine, flow]);

    const calculatedContext = {
        offerType,
        featureToggles
    } as Partial<ValidationContext>;

    // ValidationEgine expects ctx to be mutated instead of replaced.
    Object.assign(formValidation.engine.ctx, calculatedContext, ctx, { flow });

    return formValidation;
};
